import React, { useEffect, useState } from 'react';
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Divider,
  Button,
  IconButton,
  FormControl,
  Box,
  Select,
  SimpleGrid,
  Grid,
  Tag,
  TagLabel,
  TagCloseButton,
  Spinner
} from '@chakra-ui/react';
import { MdCancel, MdCheck, MdDeleteOutline, MdSearch } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { ALERT_STATUS_TYPE } from '@constants';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import {
  peopleInvolvedFieldConfig,
  transactionInvolvedFieldConfig
} from '@views/CaseInvestigationManager/utils';
import { setAlert } from '@store/actions/alertActions';
import api from '@api/endpoints';
import InputComponent from '@components/Input';
import usePromise from '@hooks/usePromise';
import {
  CASE_INVESTIGATION_CONNECTED_USER_TYPE,
  CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE
} from '@views/CaseInvestigationManager/constant';
import { formatText } from '@components/DynamicDataViewer/utils';
import { TEXT_FORMAT } from '@components/DynamicDataViewer/constant';


const ActionButton = ({
  onClick,
  isLoading,
  isDisabled,
  colorScheme,
  leftIcon,
  children
}) => (
  <Button
    variant="outline"
    colorScheme={colorScheme}
    size="sm"
    border="2px solid"
    borderRadius="xl"
    isDisabled={isDisabled}
    isLoading={isLoading}
    onClick={onClick}
    leftIcon={leftIcon}
  >
    {children}
  </Button>
);

const CreateCaseInvestigationModal = ({
  title,
  fieldsGroup,
  onClose,
  onSubmit,
  isLoading
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [peopleSearchQuery, setPeopleSearchQuery] = useState('');
  const [peopleResults, setPeopleResults] = useState([]);
  const {
    run: runUserSearch,
    isLoading: peopleLoading,
    isError: isPeopleError,
    error: peopleError
  } = usePromise();
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [externalUserForm, setExternalUserForm] = useState({
    extFullName: '',
    extContact: '',
    extEmail: '',
    extRole: 'VICTIM'
  });
  const [showAsporaUserForm, setShowAsporaUserForm] = useState(false);
  const [showExternalUserForm, setShowExternalUserForm] = useState(false);

  const [transactionSearchQuery, setTransactionSearchQuery] = useState('');
  const [transactionResults, setTransactionResults] = useState([]);
  const {
    run: runOrderSearch,
    isLoading: transactionLoading,
    isError: isTransactionError,
    error: transactionError
  } = usePromise();
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  useEffect(() => {
    const fg =
      fieldsGroup &&
      typeof fieldsGroup === 'object' &&
      !Array.isArray(fieldsGroup)
        ? fieldsGroup
        : {};
    const initial = {};
    Object.entries(fg).forEach(([provider, fields]) => {
      initial[provider] = {};
      fields.forEach((field) => {
        initial[provider][field.name] = field.value ?? '';
      });
    });
    setFormData(initial);
  }, [fieldsGroup]);

  const handleInputChange = (provider, name, value) => {
    setFormData((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [name]: value
      }
    }));
  };

  const handleFormSubmit = async () => {
    let isValid = true;

    const fg =
      fieldsGroup &&
      typeof fieldsGroup === 'object' &&
      !Array.isArray(fieldsGroup)
        ? fieldsGroup
        : {};
    for (const [provider, fields] of Object.entries(fg)) {
      for (const field of fields) {
        const shouldValidate = !field.dependsOn ||
          formData[provider]?.[field.dependsOn.field] === field.dependsOn.value;

        if (field.isRequired && shouldValidate) {
          const value = formData[provider]?.[field.name];
          if (value === undefined || value === null || value === '') {
            isValid = false;
          }
        }
      }
    }

    if (!isValid) {
      dispatch(
        setAlert({
          title: 'Please fill all required fields',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    const caseFields = formData?.Case || {};
    
    const linkedPersons = selectedPeople.map(person => {
      const isExternal = person.source !== CASE_INVESTIGATION_CONNECTED_USER_TYPE.ASPORA;
      
      if (isExternal) {
        return {
          linkType: person.role || CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM,
          isExternal: true,
          externalPerson: {
            fullName: person.name || '',
            email: person.email || '',
            phoneNumber: person.contact || person.phone || ''
          }
        };
      } else {
        return {
          userGuid: person.userId,
          linkType: person.role || CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM,
          isExternal: false
        };
      }
    });

    const linkedOrders = selectedTransactions.map(txn => ({
      orderGuid: txn.orderId || txn.id,
      linkReason: 'PRIMARY_EVENT',
      notes: ''
    }));

    const payload = {
      incidentDate: caseFields.incidentDate || new Date().toISOString(),
      priority: caseFields.priority || 'MEDIUM',
      triggerType: caseFields.triggerType,
      triggerPartner: caseFields.triggerPartner || null,
      classification: caseFields.classification,
      summary: caseFields.summary,
      linkedPersons,
      linkedOrders
    };

    await onSubmit(payload);
    onClose();
  };

  // --- People Search ---
  useEffect(() => {
    let timer;
    if (!peopleSearchQuery?.length) {
      setPeopleResults([]);
      return;
    }
    timer = setTimeout(async () => {
      runUserSearch(
        api.search
          .users({ query: peopleSearchQuery, pageNo: 0, pageSize: 5 })
          .then(({ data }) => {
            const list = Array.isArray(data?.result)
              ? data.result
              : data?.users || [];
            setPeopleResults(list);
            return data;
          })
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [peopleSearchQuery]);

  const handleUserSearchClick = () => {
    if (!peopleSearchQuery?.length) return;
    runUserSearch(
      api.search
        .users({ query: peopleSearchQuery, pageNo: 0, pageSize: 5 })
        .then(({ data }) => {
          const list = Array.isArray(data?.result)
            ? data.result
            : data?.users || [];
          setPeopleResults(list);
          return data;
        })
    );
  };

  const addPerson = (user, role = CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM) => {
    if (!user) return;
    setSelectedPeople((prev) => {
      const exists = prev.some((p) => p?.userId === user?.userId);
      if (exists) {
        dispatch(
          setAlert({
            title: 'This user is already added to the case',
            status: ALERT_STATUS_TYPE.INFO
          })
        );
        return prev;
      }
      return [
        ...prev,
        {
          userId: user?.userId,
          name:
            user?.fullName || user?.name || user?.email || String(user?.userId),
          email: user?.email,
          role,
          source: CASE_INVESTIGATION_CONNECTED_USER_TYPE.ASPORA
        }
      ];
    });
  };

  const addExternalPerson = (role = CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM) => {
    if (!externalUserForm.extFullName) {
      dispatch(
        setAlert({
          title: 'Please enter full name for external user',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    // Validate email format if provided
    if (externalUserForm.extEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(externalUserForm.extEmail)) {
      dispatch(
        setAlert({
          title: 'Please enter a valid email address',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    // Validate phone format if provided (allows +, digits, spaces, dashes, parentheses)
    if (externalUserForm.extContact && !/^\+?[\d\s\-()]+$/.test(externalUserForm.extContact)) {
      dispatch(
        setAlert({
          title: 'Please enter a valid contact number',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    if (!externalUserForm.extEmail && !externalUserForm.extContact) {
      dispatch(
        setAlert({
          title: 'Please enter at least one contact method (email or contact number)',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }
    setSelectedPeople((prev) => [
      ...prev,
      {
        userId: undefined,
        name: externalUserForm.extFullName,
        email: externalUserForm.extEmail,
        contact: externalUserForm.extContact,
        role,
        source: CASE_INVESTIGATION_CONNECTED_USER_TYPE.EXTERNAL
      }
    ]);
    setExternalUserForm({
      extFullName: '',
      extContact: '',
      extEmail: '',
      extRole: CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM
    });
  };

  const removePerson = (idx) => {
    setSelectedPeople((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSelectedPersonFieldChange = (idx, e) => {
    const { name, value } = e.target || {};
    setSelectedPeople((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [name]: value } : p))
    );
  };

  useEffect(() => {
    let timer;
    if (!transactionSearchQuery?.length) {
      setTransactionResults([]);
      return;
    }
    timer = setTimeout(async () => {
      runOrderSearch(
        api.search
          .gomsOrders(transactionSearchQuery, '', 0, 5)
          .then(({ data }) => {
            const list = Array.isArray(data?.result) ? data.result : [];
            setTransactionResults(list);
            return data;
          })
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [transactionSearchQuery]);

  const handleOrderSearchClick = () => {
    if (!transactionSearchQuery?.length) return;
    runOrderSearch(
      api.search.gomsOrders(transactionSearchQuery, '', 0, 10).then(({ data }) => {
        const list = Array.isArray(data?.result) ? data.result : [];
        setTransactionResults(list);
        return data;
      })
    );
  };

  const addTransaction = (order) => {
    if (!order) return;
    setSelectedTransactions((prev) => {
      const exists = prev.some(
        (t) => t?.orderId === (order?.orderId || order?.id)
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          orderId: order?.orderId || order?.id || String(order),
          sendAmount: order?.amount?.amount,
          receiveAmount: order?.additionalDetails?.receiveAmount?.amount,
          currencyFrom: order?.amount?.currency,
          currencyTo: order?.additionalDetails?.receiveAmount?.currency,
          orderStatus: order?.status,
          orderType: order?.product,
          userId: order?.userId,
          beneficiaryName: order?.beneficiaryName,
          transferRate: order?.transferRate,
          createdAt: order?.createdAt,
          // Keep nested objects for reference if needed
          amount: order?.amount,
          additionalDetails: order?.additionalDetails
        }
      ];
    });
  };

  const removeTransaction = (idx) => {
    setSelectedTransactions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSelectedTxnFieldChange = (idx, e) => {
    const { name, value } = e.target || {};
    setSelectedTransactions((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [name]: value } : t))
    );
  };

  return (
    <>
      <ModalHeader>{title}</ModalHeader>
      <FormControl>
        <ModalBody maxHeight="70vh" overflowY="auto">
          <VStack alignItems="stretch" spacing={6}>
            {/* Dynamic fields via config */}
            {Object.entries(
              fieldsGroup &&
                typeof fieldsGroup === 'object' &&
                !Array.isArray(fieldsGroup)
                ? fieldsGroup
                : {}
            ).map(([provider, fields]) => (
              <VStack
                key={provider}
                p={4}
                spacing={4}
                border={'1px dashed'}
                borderColor={'whiteAlpha.500'}
                borderRadius={'xl'}
                alignItems="stretch"
              >
                {fields.map((field) => {
                  // Check if field has conditional rendering
                  const shouldShow = !field.dependsOn ||
                    formData[provider]?.[field.dependsOn.field] === field.dependsOn.value;

                  if (!shouldShow) return null;

                  return (
                    <DynamicFormField
                      key={field.name}
                      fieldConfig={field}
                      data={formData[provider] ?? ''}
                      handleChange={(e) => {
                        const { name, value, type, checked } = e.target;
                        handleInputChange(
                          provider,
                          name,
                          type === 'checkbox'
                            ? checked
                            : type === 'number'
                            ? Number(value)
                            : value
                        );
                      }}
                    />
                  );
                })}
              </VStack>
            ))}

            {/* People Involved */}
            <VStack
              p={4}
              spacing={4}
              border={'1px dashed'}
              borderColor={'whiteAlpha.500'}
              borderRadius={'xl'}
              alignItems="stretch"
            >
              <Text fontSize="md" fontWeight="semibold">
                People Involved
              </Text>
              <Divider />

              {/* Action Buttons */}
              <HStack spacing={3} justify="flex-start">
                <Button
                  size="sm"
                  colorScheme={'brand'}
                  bg="backGround"
                  border={'2px solid'}
                  borderColor={'whiteAlpha.500'}
                  borderRadius={'xl'}
                  onClick={() => {
                    setShowAsporaUserForm(!showAsporaUserForm);
                    setShowExternalUserForm(false);
                  }}
                  variant={showAsporaUserForm ? 'solid' : 'outline'}
                >
                  {showAsporaUserForm ? 'Hide' : 'Add'} Aspora User
                </Button>
                <Button
                  size="sm"
                  colorScheme={'brand'}
                  bg="backGround"
                  border={'2px solid'}
                  borderColor={'whiteAlpha.500'}
                  borderRadius={'xl'}
                  onClick={() => {
                    setShowExternalUserForm(!showExternalUserForm);
                    setShowAsporaUserForm(false);
                  }}
                  variant={showExternalUserForm ? 'solid' : 'outline'}
                >
                  {showExternalUserForm ? 'Hide' : 'Add'} External User
                </Button>
              </HStack>

              {/* Aspora User Section */}
              {showAsporaUserForm && (
                <Box>
                  <Text fontSize="sm" mb={2} fontWeight="semibold">
                    Add Aspora User
                  </Text>
                  <Grid
                    templateColumns={{ base: '1fr', md: '1fr auto' }}
                    gap={3}
                    mb={3}
                    alignItems="center"
                  >
                    <InputComponent
                      size="sm"
                      type="text"
                      value={peopleSearchQuery}
                      name="search_users"
                      handleChange={(e) => setPeopleSearchQuery(e.target.value)}
                      placeholder="Search by name, email, or mobile number...."
                      rightElement={<MdSearch size="1rem" color="white" />}
                      width="100%"
                      autoComplete={'off'}
                      spellCheck={false}
                      autoCorrect={'off'}
                    />
                    <Button
                      colorScheme={'brand'}
                      size="sm"
                      fontSize={'xs'}
                      bg="backGround"
                      border={'2px solid'}
                      borderColor={'whiteAlpha.500'}
                      borderRadius={'xl'}
                      onClick={handleUserSearchClick}
                      isDisabled={!peopleSearchQuery?.length}
                      justifySelf="end"
                    >
                      Search User
                    </Button>
                  </Grid>
                  <Box
                    bg="backGround"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="lg"
                    p={3}
                  >
                    {peopleLoading ? (
                      <HStack justify="center" py={6}>
                        <Spinner size="sm" />
                        <Text fontSize="sm" color="whiteAlpha.700">
                          Searching users…
                        </Text>
                      </HStack>
                    ) : peopleError ? (
                      <Text fontSize="sm" color="red.300">
                        {peopleError?.message}
                      </Text>
                    ) : !peopleResults?.length ? (
                      <Text fontSize="sm" color="whiteAlpha.600">
                        No users found
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={2}>
                        {peopleResults.slice(0, 5).map((u) => (
                          <HStack
                            key={u?.userId}
                            justify="space-between"
                            bg="altGray.200"
                            border="1px solid"
                            borderColor="whiteAlpha.300"
                            borderRadius="md"
                            p={2}
                          >
                            <Box>
                              <Text fontSize="sm" fontWeight="medium">
                                {u?.fullName || u?.name || u?.email || u?.userId}
                              </Text>
                              <Text fontSize="xs" color="whiteAlpha.600">
                                {u?.email}
                              </Text>
                            </Box>
                            <HStack>
                              <Select
                                size="sm"
                                bg="backGround"
                                border="2px solid"
                                borderColor="whiteAlpha.500"
                                borderRadius="lg"
                                defaultValue={CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM}
                                onChange={(e) => {
                                  u.__role = e.target.value;
                                }}
                              >
                                {
                                  Object.entries(CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE)?.map(([key, value]) => (
                                    <option value={key}>
                                      {`Add as ${formatText(value, TEXT_FORMAT.UPPER_CASE)}`}
                                    </option>
                                  ))
                                }
                              </Select>
                              <Button
                                colorScheme={'brand'}
                                size="sm"
                                bg="backGround"
                                border={'2px solid'}
                                borderColor={'whiteAlpha.500'}
                                borderRadius={'lg'}
                                onClick={() =>
                                  addPerson(u, u.__role || CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM)
                                }
                              >
                                Add
                              </Button>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </Box>
              )}

              {/* External User Section */}
              {showExternalUserForm && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Add External User
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={4}>
                    {peopleInvolvedFieldConfig.externalUserFields.map((f) => (
                      <Box key={f.key}>
                        <DynamicFormField
                          fieldConfig={f}
                          data={externalUserForm}
                          handleChange={(e) =>
                            setExternalUserForm((prev) => ({
                              ...prev,
                              [e.target.name]: e.target.value
                            }))
                          }
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                  <HStack mt={2}>
                    <Button
                      size="sm"
                      colorScheme={'brand'}
                      bg="backGround"
                      border={'2px solid'}
                      borderColor={'whiteAlpha.500'}
                      borderRadius={'xl'}
                      onClick={() =>
                        addExternalPerson(externalUserForm.extRole || CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM)
                      }
                    >
                      Add External User
                    </Button>
                  </HStack>
                </Box>
              )}

              {/* Chips for added users */}
              {selectedPeople?.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Selected People
                  </Text>
                  <HStack wrap="wrap" spacing={2}>
                    {selectedPeople.map((p, idx) => (
                      <Tag
                        key={`chip_${p.name}_${idx}`}
                        size="sm"
                        borderRadius="full"
                        variant="subtle"
                        colorScheme={p.role === CASE_INVESTIGATION_CONNECTED_USER_TYPE_ROLE.VICTIM ? 'green' : 'orange'}
                      >
                        <TagLabel>
                          {p.name} ({p.role})
                        </TagLabel>
                        <TagCloseButton onClick={() => removePerson(idx)} />
                      </Tag>
                    ))}
                  </HStack>
                </Box>
              )}
            </VStack>

            {/* Transactions Involved */}
            <VStack
              p={4}
              spacing={4}
              border={'1px dashed'}
              borderColor={'whiteAlpha.500'}
              borderRadius={'xl'}
              alignItems="stretch"
            >
              <Text fontSize="md" fontWeight="semibold">
                Transaction Involved
              </Text>
              <Divider />
              <Grid
                templateColumns={{ base: '1fr', md: '1fr auto' }}
                gap={3}
                alignItems="center"
              >
                <InputComponent
                  type="text"
                  size="sm"
                  value={transactionSearchQuery}
                  name="search_orders"
                  handleChange={(e) =>
                    setTransactionSearchQuery(e.target.value)
                  }
                  placeholder="Search by Order ID"
                  rightElement={<MdSearch size="1rem" color="white" />}
                  width="100%"
                  autoComplete={'off'}
                  spellCheck={false}
                  autoCorrect={'off'}
                />
                <Button
                  colorScheme={'brand'}
                  size="sm"
                  fontSize={'xs'}
                  bg="backGround"
                  border={'2px solid'}
                  borderColor={'whiteAlpha.500'}
                  borderRadius={'xl'}
                  onClick={handleOrderSearchClick}
                  isDisabled={!transactionSearchQuery?.length}
                  justifySelf="end"
                >
                  Search Order
                </Button>
              </Grid>
              <Box
                bg="backGround"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="lg"
                p={3}
              >
                {transactionLoading ? (
                  <HStack justify="center" py={6}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="whiteAlpha.700">
                      Searching orders…
                    </Text>
                  </HStack>
                ) : !!transactionError ? (
                  <Text fontSize="sm" color="red.300">
                    {transactionError?.message}
                  </Text>
                ) : !transactionResults?.length ? (
                  <Text fontSize="sm" color="whiteAlpha.600">
                    No transactions found
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {transactionResults.slice(0, 5).map((o) => (
                      <HStack
                        key={o?.order_id || o?.id}
                        justify="space-between"
                        bg="altGray.200"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        borderRadius="md"
                        p={2}
                      >
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" color="white">
                            {o?.orderId || o?.id}
                          </Text>
                          <Text fontSize="xs" color="whiteAlpha.600">
                            {o?.amount?.amount} {o?.amount?.currency} →{' '}
                            {o?.additionalDetails?.receiveAmount?.amount} {o?.additionalDetails?.receiveAmount?.currency}
                          </Text>
                          <Text fontSize="xs" color="whiteAlpha.500">
                            Status: {o?.status} • {o?.product}
                          </Text>
                        </Box>
                        <Button
                          colorScheme={'brand'}
                          size="sm"
                          bg="backGround"
                          border={'2px solid'}
                          borderColor={'whiteAlpha.500'}
                          borderRadius={'lg'}
                          onClick={() => addTransaction(o)}
                        >
                          Add
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>

              {selectedTransactions?.length ? (
                <VStack align="stretch" spacing={3} mt={2}>
                  {selectedTransactions.map((t, idx) => {
                    const txFields = transactionInvolvedFieldConfig.selectedTransactionFields(t);
                    return (
                      <Box
                        key={`txn_${idx}`}
                        bg="backGround"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        borderRadius="lg"
                        p={3}
                      >
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                          {txFields.map((f) => (
                            <Box key={f.key}>
                              <DynamicFormField
                                key={f.key}
                                fieldConfig={f}
                                data={t}
                                handleChange={(e) =>
                                  handleSelectedTxnFieldChange(idx, e)
                                }
                              />
                            </Box>
                          ))}
                        </SimpleGrid>
                        <HStack justify="flex-end" mt={2}>
                          <IconButton
                            icon={<MdDeleteOutline />}
                            colorScheme={'red'}
                            size="sm"
                            bgColor="whiteAlpha.300"
                            border="1px solid"
                            borderColor="whiteAlpha.300"
                            borderRadius="full"
                            p={0}
                            onClick={() => removeTransaction(idx)}
                            aria-label={`Remove ${idx}`}
                          />
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              ) : (
                <Text fontSize="xs" color="whiteAlpha.600">
                  No transactions selected
                </Text>
              )}
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <ActionButton
              onClick={onClose}
              isDisabled={isLoading}
              isLoading={false}
              colorScheme="red"
              leftIcon={<MdCancel />}
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleFormSubmit}
              isLoading={isLoading}
              isDisabled={isLoading}
              colorScheme="brand"
              leftIcon={<MdCheck />}
            >
              Submit
            </ActionButton>
          </HStack>
        </ModalFooter>
      </FormControl>
    </>
  );
};

export default CreateCaseInvestigationModal;
